import React, { useEffect, useMemo, useState } from "react";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import CustomButton from "components/common/Button/CustomButton";
import FormCategorySelect from "./FormCategorySelect";
import {
  extractFormCategoriesFromResponse,
  normalizeFormCategoryOptions,
  resolveFormCategoryIdFromOption,
  resolveFormCategoryOptionForTemplate,
} from "./formCategoryUtils";
import {
  buildDefaultCloneTitle,
  cloneFormTemplateRequest,
} from "./formTemplateClone";
import apiCall from "store/services/sp.services";
import api from "store/services/sil.services";
import { toast } from "react-toastify";

const FormTemplateCloneModal = ({
  isOpen,
  onClose,
  template,
  role,
  routeRole,
  onCloned,
  prefetchedCategories = null,
}) => {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formCatError, setFormCatError] = useState("");

  const effectiveRole = role === "sil" ? "sil" : routeRole || role || "crm";

  useEffect(() => {
    if (!isOpen || !template) {
      return;
    }

    setTitle(buildDefaultCloneTitle(template.title));
    setDescription(template.description ?? "");
    setFormCatError("");
    setSelectedCategory(null);

    const loadCategories = async () => {
      setCategoriesLoading(true);
      try {
        let rawCategories = Array.isArray(prefetchedCategories)
          ? prefetchedCategories
          : extractFormCategoriesFromResponse(prefetchedCategories);

        if (!rawCategories.length) {
          const res =
            effectiveRole === "sil"
              ? await api.silFormCategoriesApi()
              : await apiCall.formCategoriesRequest({ role: effectiveRole });
          rawCategories = extractFormCategoriesFromResponse(res);
        }

        const list = normalizeFormCategoryOptions(rawCategories);
        setCategories(list);

        const match = resolveFormCategoryOptionForTemplate(
          template,
          list,
          effectiveRole
        );
        if (match) {
          setSelectedCategory(match);
        }
      } catch (err) {
        toast.error(err?.message || "Failed to load form categories.");
      } finally {
        setCategoriesLoading(false);
      }
    };

    loadCategories();
  }, [isOpen, template, effectiveRole, prefetchedCategories]);

  const selectedCategoryId = useMemo(
    () => resolveFormCategoryIdFromOption(selectedCategory),
    [selectedCategory]
  );

  const canSubmit = useMemo(() => {
    return (
      title.trim().length > 0 &&
      selectedCategoryId != null &&
      !submitting &&
      !categoriesLoading
    );
  }, [title, selectedCategoryId, submitting, categoriesLoading]);

  const handleSubmit = async () => {
    if (!template) {
      return;
    }
    if (!title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (selectedCategoryId == null) {
      setFormCatError("Form category is required.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await cloneFormTemplateRequest({
        role: effectiveRole === "sil" ? "sil" : routeRole,
        routeRole: effectiveRole === "sil" ? "sil" : routeRole,
        template,
        overrides: {
          title: title.trim(),
          description: description.trim(),
          form_category_id: selectedCategoryId,
        },
      });

      if (res.status !== 201 && res.status !== 200) {
        throw new Error(
          res?.data?.error || res?.data?.message || "Failed to clone form."
        );
      }

      const clonedForm = res?.data?.data;
      toast.success("Form copy created.");
      onCloned?.({ clonedForm, sourceTitle: template.title });
      onClose();
    } catch (err) {
      toast.error(err?.message || "Failed to clone form.");
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div className='flex justify-end gap-2'>
      <CustomButton
        type='button'
        label='Cancel'
        variant='secondary'
        isDisabled={submitting}
        clickHandler={onClose}
      />
      <CustomButton
        type='button'
        label='Create copy'
        variant='primary'
        showLoading={submitting}
        isDisabled={!canSubmit}
        clickHandler={handleSubmit}
      />
    </div>
  );

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title='Clone form'
      closeModal={submitting ? () => {} : onClose}
      cssClass='w-[40%] max-w-lg sm:max-w-xl z-[99999]'
      modalFooter={footer}
    >
      <div className='space-y-4 text-left'>
        <p className='text-sm text-gray-600'>
          Set the name and category for the new copy. You can change fields and
          sections after it opens in the form builder.
        </p>
        {template?.title && (
          <p className='text-xs text-gray-500'>
            Copying from: <span className='font-medium'>{template.title}</span>
          </p>
        )}

        <div className='did-floating-label-content'>
          <label htmlFor='clone-form-title' className='did-floating-label'>
            Title
          </label>
          <input
            id='clone-form-title'
            type='text'
            className='did-floating-input w-full'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Form category
          </label>
          <FormCategorySelect
            categories={categories}
            selectedOption={selectedCategory}
            onCategoryCreated={(category) => {
              setCategories((prev) => [...prev, category]);
            }}
            onChange={(option) => {
              setSelectedCategory(option);
              setFormCatError("");
            }}
            role={effectiveRole === "sil" ? "sil" : effectiveRole}
            isDisabled={submitting || categoriesLoading}
            menuInModal
          />
          {formCatError && (
            <p className='text-sm text-red-600 mt-1'>{formCatError}</p>
          )}
        </div>

        <div className='did-floating-label-content'>
          <label htmlFor='clone-form-description' className='did-floating-label'>
            Description
          </label>
          <textarea
            id='clone-form-description'
            className='did-floating-input w-full min-h-[88px]'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>
    </ModalSkeleton>
  );
};

export default FormTemplateCloneModal;
